import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, Loader2 } from 'lucide-react';
// BUG-20260722-000251 (T-5, RC-005): the answer-submit catch below is the
// most product-critical async error path in the app, but was previously
// recorded only in the student's own devtools console (RCA reviewer
// correction: window listeners never see an already-caught async error —
// this catch must be instrumented directly).
import { reportClientError } from '@/lib/errorReporter';

// show-not-tell/TS-004 (T4) / show-not-tell/US-004 (T5): added aria-label to the
// icon-only Send button so it meets the WCAG 2.1 AA icon-only-control requirement
// when reused (verbatim, no other change) by CoachingConversation.tsx.

interface ChatInputProps {
  disabled: boolean;
  onSubmit: (content: string) => Promise<void>;
}

export function ChatInput({ disabled, onSubmit }: ChatInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedValue = inputValue.trim();
    if (!trimmedValue || disabled || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(trimmedValue);
      setInputValue(''); // Clear input on success
    } catch (error) {
      console.error('Error submitting answer:', error);
      // BUG-20260722-000251 (T-5): also send this to the off-device reporter
      // (type 'error', metric-eligible) — never the answer text itself, only
      // the error's own name/message.
      reportClientError({
        name: error instanceof Error ? error.name : 'Error',
        message: error instanceof Error ? error.message : String(error),
        source: 'answer_submit',
      });
      // Keep input value so user can retry
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const isDisabled = disabled || isSubmitting;
  const placeholder = disabled ? 'Lesson complete!' : 'Type your answer...';

  return (
    <form onSubmit={handleSubmit} className="border-t bg-white p-4">
      <div className="flex gap-2 max-w-3xl mx-auto">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isDisabled}
          className="flex-1"
          autoFocus
        />
        <Button
          type="submit"
          disabled={isDisabled || !inputValue.trim()}
          size="icon"
          aria-label="Send message"
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </form>
  );
}
